/**
 * JSONPath Tester Service
 * Evaluates JSONPath expressions against JSON data
 */

import { JSONPath } from 'jsonpath-plus';
import { EncodingOperation, EncoderToolType } from '../types/EncodingOperation';
import { ValidationError, ProcessingError } from '../utils/errorHandler';

export interface JSONPathResult {
  path: string;
  value: any;
  parent?: any;
  parentProperty?: string | number;
}

export interface ExampleQuery {
  name: string;
  query: string;
  description: string;
}

export class JSONPathTesterService {
  private static operationCounter = 0;

  /**
   * Evaluate a JSONPath expression against JSON data
   */
  static async evaluate(
    jsonData: string,
    jsonpathQuery: string
  ): Promise<EncodingOperation> {
    const operationId = `jsonpath-tester-${Date.now()}-${++this.operationCounter}`;

    try {
      // Validate inputs
      this.validateInput(jsonData, jsonpathQuery);

      // Parse JSON
      let parsedData: any;
      try {
        parsedData = JSON.parse(jsonData);
      } catch (error) {
        throw new ValidationError(
          `Invalid JSON: ${error instanceof Error ? error.message : 'Parse error'}`,
          EncoderToolType.JSONPATH_TESTER
        );
      }

      // Execute JSONPath query
      let result: any;
      let resultPaths: string[] = [];
      let resultValues: any[] = [];

      try {
        // Get both values and paths
        resultValues = JSONPath({
          path: jsonpathQuery,
          json: parsedData,
          wrap: false
        });

        resultPaths = JSONPath({
          path: jsonpathQuery,
          json: parsedData,
          resultType: 'path'
        });

        // Construct detailed result
        result = {
          values: resultValues,
          paths: resultPaths,
          count: Array.isArray(resultValues) ? resultValues.length : (resultValues !== undefined ? 1 : 0)
        };
      } catch (error) {
        throw new ValidationError(
          `Invalid JSONPath query: ${error instanceof Error ? error.message : 'Query error'}`,
          EncoderToolType.JSONPATH_TESTER
        );
      }

      const outputJson = JSON.stringify(result, null, 2);

      return {
        id: operationId,
        toolType: EncoderToolType.JSONPATH_TESTER,
        operationType: 'encode',
        inputData: jsonData,
        outputData: outputJson,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          query: jsonpathQuery,
          resultCount: result.count,
          inputLength: jsonData.length,
          outputLength: outputJson.length,
          hasResults: result.count > 0
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        error instanceof Error ? error.message : 'JSONPath evaluation failed',
        EncoderToolType.JSONPATH_TESTER
      );
    }
  }

  /**
   * Validate input parameters
   */
  private static validateInput(jsonData: string, jsonpathQuery: string): void {
    if (!jsonData || jsonData.trim() === '') {
      throw new ValidationError('JSON data cannot be empty', EncoderToolType.JSONPATH_TESTER);
    }

    if (!jsonpathQuery || jsonpathQuery.trim() === '') {
      throw new ValidationError('JSONPath query cannot be empty', EncoderToolType.JSONPATH_TESTER);
    }

    // Check size limits
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (jsonData.length > maxSize) {
      throw new ValidationError(
        `JSON data exceeds maximum size of ${maxSize / (1024 * 1024)}MB`,
        EncoderToolType.JSONPATH_TESTER
      );
    }

    if (jsonpathQuery.length > 1000) {
      throw new ValidationError('JSONPath query exceeds maximum length of 1000 characters', EncoderToolType.JSONPATH_TESTER);
    }
  }

  /**
   * Get example JSONPath queries
   */
  static getExampleQueries(): ExampleQuery[] {
    return [
      {
        name: 'Root',
        query: '$',
        description: 'Select the root object'
      },
      {
        name: 'All Properties',
        query: '$.*',
        description: 'Select all properties of the root object'
      },
      {
        name: 'Specific Property',
        query: '$.store.book',
        description: 'Select the book array from store'
      },
      {
        name: 'Array Index',
        query: '$.store.book[0]',
        description: 'Select the first book'
      },
      {
        name: 'Array Slice',
        query: '$.store.book[0:2]',
        description: 'Select the first two books'
      },
      {
        name: 'Last Element',
        query: '$.store.book[-1]',
        description: 'Select the last book'
      },
      {
        name: 'All Books',
        query: '$..book',
        description: 'Recursively select all book arrays'
      },
      {
        name: 'All Authors',
        query: '$..author',
        description: 'Recursively select all author properties'
      },
      {
        name: 'Filter by Price',
        query: '$.store.book[?(@.price < 10)]',
        description: 'Select books with price less than 10'
      },
      {
        name: 'Filter by Category',
        query: '$.store.book[?(@.category === "fiction")]',
        description: 'Select fiction books'
      },
      {
        name: 'Multiple Conditions',
        query: '$.store.book[?(@.price < 10 && @.category === "fiction")]',
        description: 'Select fiction books under $10'
      },
      {
        name: 'Has Property',
        query: '$.store.book[?(@.isbn)]',
        description: 'Select books that have an ISBN'
      },
      {
        name: 'All Prices',
        query: '$..price',
        description: 'Recursively select all price properties'
      },
      {
        name: 'Wildcard Array',
        query: '$.store.book[*].author',
        description: 'Select all book authors'
      },
      {
        name: 'Union',
        query: '$.store.book[0,2]',
        description: 'Select first and third book'
      }
    ];
  }

  /**
   * Get sample JSON data for testing
   */
  static getSampleJSON(): string {
    return JSON.stringify(
      {
        store: {
          book: [
            {
              category: 'reference',
              author: 'Nigel Rees',
              title: 'Sayings of the Century',
              price: 8.95
            },
            {
              category: 'fiction',
              author: 'Evelyn Waugh',
              title: 'Sword of Honour',
              price: 12.99
            },
            {
              category: 'fiction',
              author: 'Herman Melville',
              title: 'Moby Dick',
              isbn: '0-553-21311-3',
              price: 8.99
            },
            {
              category: 'fiction',
              author: 'J. R. R. Tolkien',
              title: 'The Lord of the Rings',
              isbn: '0-395-19395-8',
              price: 22.99
            }
          ],
          bicycle: {
            color: 'red',
            price: 19.95
          }
        }
      },
      null,
      2
    );
  }

  /**
   * Clear cache (for testing)
   */
  static clearCache(): void {
    this.operationCounter = 0;
  }
}
